import { useEffect, useState, type ReactNode } from 'react';
import { SEO } from '@/components/SEO';
import { trackHq } from './analytics';
import { HQ_NAME } from './config';
import { currentHqPath } from './detect';
import { HqMark } from './HqMark';
import { HqLink } from './Link';
import { HQ_SHELL } from './theme';
import { harborUrl, hqUrl } from './urls';

export function useHqPath(): string {
  const [path, setPath] = useState(currentHqPath);
  useEffect(() => {
    const onPop = () => setPath(currentHqPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return path;
}

export function HqLayout({
  title,
  description,
  path,
  jsonLd,
  children,
}: {
  title: string;
  description: string;
  path: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  children: ReactNode;
}) {
  const canonicalPath = path === '/start' ? '/' : path;
  const current = useHqPath();

  return (
    <div className='min-h-screen bg-hq-cream text-hq-ink'>
      <SEO
        title={title}
        description={description}
        path={canonicalPath}
        baseUrl='https://coparentinghq.com'
        siteName={HQ_NAME}
        jsonLd={jsonLd}
      />
      <header className='sticky top-0 z-20 border-b border-hq-border/80 bg-hq-cream/90 backdrop-blur-sm'>
        <div className={`${HQ_SHELL} flex items-center justify-between py-3.5`}>
          <HqLink to='/' className='flex items-center gap-2.5'>
            <HqMark className='h-8 w-8 shrink-0' />
            <span className='font-hq text-lg font-medium tracking-tight sm:text-xl'>{HQ_NAME}</span>
          </HqLink>
          <nav className='flex items-center gap-4 text-sm sm:gap-5'>
            <HqLink
              to='/tools'
              className={
                current === '/tools' ? 'font-semibold text-hq-ink' : 'text-hq-muted hover:text-hq-ink'
              }
            >
              Tools
            </HqLink>
            <HqLink
              to='/guides'
              className={
                current.startsWith('/guides')
                  ? 'font-semibold text-hq-ink'
                  : 'text-hq-muted hover:text-hq-ink'
              }
            >
              Guides
            </HqLink>
            <HqLink
              to='/about'
              className={
                current === '/about' ? 'font-semibold text-hq-ink' : 'text-hq-muted hover:text-hq-ink'
              }
            >
              About
            </HqLink>
            <a
              href={harborUrl('/')}
              className='hidden text-hq-muted hover:text-hq-ink sm:inline'
              onClick={() => trackHq('HQ Harbor CTA Pressed', { location: 'header' })}
            >
              Harbor
            </a>
          </nav>
        </div>
      </header>

      <main className={`${HQ_SHELL} py-10 sm:py-14`}>{children}</main>

      <footer className='border-t border-hq-border bg-[#EFE8DC]'>
        <div className={`${HQ_SHELL} grid gap-8 py-10 sm:grid-cols-4`}>
          <div className='sm:col-span-2'>
            <p className='flex items-center gap-2 font-hq text-lg font-medium'>
              <HqMark className='h-7 w-7' />
              {HQ_NAME}
            </p>
            <p className='mt-3 max-w-md text-sm leading-relaxed text-hq-muted'>
              A free starting place for coparents. Tools and guides you can use without an
              account. Built by the team behind{' '}
              <a
                href={harborUrl('/')}
                className='font-medium text-hq-sage hover:underline'
                onClick={() => trackHq('HQ Harbor CTA Pressed', { location: 'footer' })}
              >
                Harbor
              </a>
              . {hqUrl('/')}
            </p>
          </div>
          <div>
            <p className='text-xs font-semibold uppercase tracking-[0.14em] text-hq-legal'>On this site</p>
            <ul className='mt-3 space-y-2 text-sm'>
              <li>
                <HqLink to='/tools' className='text-hq-muted hover:text-hq-ink'>
                  Tools
                </HqLink>
              </li>
              <li>
                <HqLink to='/guides' className='text-hq-muted hover:text-hq-ink'>
                  Guides
                </HqLink>
              </li>
              <li>
                <HqLink to='/about' className='text-hq-muted hover:text-hq-ink'>
                  About
                </HqLink>
              </li>
            </ul>
          </div>
          <div>
            <p className='text-xs font-semibold uppercase tracking-[0.14em] text-hq-legal'>Harbor</p>
            <ul className='mt-3 space-y-2 text-sm'>
              <li>
                <a
                  href={harborUrl('/')}
                  className='text-hq-muted hover:text-hq-ink'
                  onClick={() => trackHq('HQ Harbor CTA Pressed', { location: 'footer-nav' })}
                >
                  Shared calendar and ledger
                </a>
              </li>
              <li>
                <a
                  href={harborUrl('/parenting-plan/builder')}
                  className='text-hq-muted hover:text-hq-ink'
                  onClick={() => trackHq('HQ Harbor CTA Pressed', { location: 'footer-nav' })}
                >
                  Parenting plan
                </a>
              </li>
              <li>
                <a
                  href={harborUrl('/divorce')}
                  className='text-hq-muted hover:text-hq-ink'
                  onClick={() => trackHq('HQ Harbor CTA Pressed', { location: 'footer-nav' })}
                >
                  Divorce checklist
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className={`${HQ_SHELL} border-t border-hq-border py-6 text-xs leading-relaxed text-hq-legal`}>
          <p>Not legal advice. If you are in danger, call local emergency services.</p>
          <p className='mt-1'>In the US: 988, or 1-800-799-7233 for the domestic violence hotline.</p>
        </div>
      </footer>
    </div>
  );
}
